import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import Radio from "components/radio";
import { Select } from "components/select/index";
import { HouseAccountType } from "pages/secure/facility/customer/context/contextTypes";
import "./moveHouseAccountPaymentsSheet.scss";

export type HouseAccountMoveChoice = "keep" | "move";

function formatHouseAccountOptionLabel(account: HouseAccountType): string {
  return account.account_number ? `${account.title} (${account.account_number})` : account.title;
}

interface IMoveHouseAccountPaymentsSheetProps {
  open: boolean;
  loading: boolean;
  newCustomerName: string;
  accounts: HouseAccountType[];
  onCancel: () => void;
  onConfirm: (choice: HouseAccountMoveChoice, selectedAccountId?: number) => void;
}

function MoveHouseAccountPaymentsSheet(props: IMoveHouseAccountPaymentsSheetProps) {
  const { t } = useTranslation();
  const { Option } = Select;
  const [choice, setChoice] = useState<HouseAccountMoveChoice>("keep");
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const canMove = props.accounts.length > 0;
  const moveDisabled = choice === "move" && (selectedAccountId == null || !canMove);

  useEffect(() => {
    if (!props.open) {
      setChoice("keep");
      setSelectedAccountId(null);
      return;
    }

    if (props.accounts.length === 1 && props.accounts[0]?.id != null) {
      setSelectedAccountId(props.accounts[0].id);
    }
  }, [props.open, props.accounts]);

  useEffect(() => {
    if (!canMove && choice === "move") {
      setChoice("keep");
    }
  }, [canMove, choice]);

  return (
    <Sheet
      title={t("House account payments")}
      open={props.open}
      size="medium"
      closable
      stacked
      onCancel={props.onCancel}
      onOk={() => props.onConfirm(choice, selectedAccountId ?? undefined)}
      okText={t("Update customer")}
      okDisabled={moveDisabled || props.loading}
      okLoading={props.loading}
      overflow
    >
      <div className="move-house-account-payments">
        <p className="move-house-account-payments__description">
          {t(
            "This order has house account payment(s). Choose whether to keep them on the original account(s) or move them to an account for {{customerName}}.",
            { customerName: props.newCustomerName || "—" },
          )}
        </p>

        <Radio.Group
          name="house-account-move-choice"
          className="move-house-account-payments__choices"
          value={choice}
          onChange={(value: HouseAccountMoveChoice) => setChoice(value)}
        >
          <Radio.Button value="keep" check>
            {t("Keep payments on original house account(s)")}
          </Radio.Button>
          <Radio.Button value="move" disabled={!canMove} check>
            {t("Move all house account payments to another account")}
          </Radio.Button>
        </Radio.Group>

        {choice === "move" && canMove ? (
          <div className="move-house-account-payments__account-select">
            <Select
              block
              usePortal
              dropdownMinWidth={360}
              dropdownClassName="move-house-account-payments__dropdown"
              label={t("House account")}
              key={`house-account-${String(selectedAccountId)}-${props.accounts.map(account => account.id).join("-")}`}
              defaultValue={selectedAccountId ?? undefined}
              onChange={(value: number) => setSelectedAccountId(value)}
              placeholder={t("Select a house account")}
            >
              {props.accounts.map(account => (
                <Option
                  key={account.id}
                  value={account.id}
                  name={formatHouseAccountOptionLabel(account)}
                  className="move-house-account-payments__option"
                >
                  <span className="move-house-account-payments__option-title">{account.title}</span>
                  {account.account_number ? (
                    <span className="move-house-account-payments__option-meta">{account.account_number}</span>
                  ) : null}
                </Option>
              ))}
            </Select>
          </div>
        ) : null}

        {choice === "move" && !canMove ? (
          <p className="move-house-account-payments__helper">
            {t("The selected customer does not have any open house accounts to move payments to.")}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

export default MoveHouseAccountPaymentsSheet;
